/**
 * SecretsManagerPageV2 — Administer → Secrets Manager (Phase 1).
 *
 * <p>Lets Org / Tenant Admins store credentials in the configured vault and get
 * back an opaque {@code vault://...} reference to paste into Connector / Agent /
 * Skill fields. Plaintext never leaves the vault — this UI only handles
 * metadata, references, and lifecycle (add / rotate / delete / copy ref).</p>
 *
 * <p>Backend surface is unchanged from the existing {@code SecretController}
 * (POST/exists/rotate/delete have been on disk since the secrets package
 * landed). Phase 1 adds {@code GET /api/v1/secrets} (list) + a default
 * {@code SecretBackend.list()} so the LocalEncryptedSecretBackend reports its
 * contents; remote backends (GCP / HashiCorp / AWS / Azure) report empty until
 * Phase 3 implements their per-vendor list() impls. Status tiles for each
 * backend come from the pre-existing {@code GET /api/v1/secrets/backends}.</p>
 *
 * <p>Phase 2 (org-admin self-service config of GCP project ID, HashiCorp URL,
 * AWS region, etc.) is queued; the tiles below show "Configured via yaml" for
 * now and clicking the inactive ones surfaces the relevant property name.</p>
 *
 * <p>Per CLAUDE_PROJECT_RULES §5 (V1↔V2 redesign discipline), this is a new V2
 * page — there's no V1 ancestor to keep functional parity with. It only reads
 * from + writes to the existing backend endpoints, which are themselves
 * unchanged for this phase aside from the additive GET /secrets endpoint.</p>
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  RefreshCw, Plus, Key, Copy, Check, RotateCcw, Trash2, Cloud, Database,
  Shield, AlertTriangle, Info, Lock, Eye, EyeOff,
} from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../services/api';
import {
  Button, Pill, Card, Modal, Slideout, Table, EmptyState, MetricCard,
} from '../components/v2';

/* Backend → display metadata. Stable across Phase 1-3. */
const BACKEND_META = {
  local:     { label: 'Local (encrypted)',  icon: Database, tone: 'muted',   note: 'In-process AES-256-GCM. Dev / single-node only.' },
  gcp:       { label: 'GCP Secret Manager', icon: Cloud,    tone: 'trust',   note: 'Google Cloud Secret Manager.' },
  hashicorp: { label: 'HashiCorp Vault',    icon: Shield,   tone: 'action',  note: 'HashiCorp Vault (KV v2).' },
  aws:       { label: 'AWS Secrets Manager',icon: Cloud,    tone: 'warning', note: 'AWS Secrets Manager.' },
  azure:     { label: 'Azure Key Vault',    icon: Cloud,    tone: 'cyan',    note: 'Azure Key Vault.' },
};
const KNOWN_BACKENDS = Object.keys(BACKEND_META);

/* Format a backend-specific path into a friendly { name, scope } pair.
 * Path convention from SecretManagerService.store: tenant-<x>/<resourceType>/<resourceId>/<fieldName>.
 * The last segment is the field; the second-to-last is the resource id; we
 * pull the resourceType from earlier in the path for the breadcrumb. */
function describePath(path) {
  if (!path) return { name: '—', scope: '' };
  const parts = path.split('/');
  const name = parts[parts.length - 1] || path;
  const scope = parts.slice(0, -1).join(' / ') || '—';
  return { name, scope };
}

function fmtRelative(s) {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  const diff = d.getTime() - Date.now();
  const days = Math.round(diff / 86400000);
  if (Math.abs(days) === 0) return 'today';
  if (days > 0) return `in ${days}d`;
  return `${-days}d ago`;
}

export default function SecretsManagerPageV2() {
  const { addToast } = useToast();
  const { hasPermission } = useAuth();

  const canWrite = hasPermission && hasPermission('secrets.write');
  const canAdmin = hasPermission && hasPermission('secrets.admin');

  const [backendsState, setBackendsState] = useState({ available: [], default: null, configuredForOrg: [] });
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const [addOpen, setAddOpen] = useState(false);
  const [rotateTarget, setRotateTarget] = useState(null); // { wireRef, displayName }
  const [deleteTarget, setDeleteTarget] = useState(null); // { wireRef, displayName }
  // Phase 2 — per-backend configure slideout. `null` when closed; backend id
  // string when open. Slideout reads + writes /api/v1/secrets/backends/{id}/config.
  const [configureTarget, setConfigureTarget] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, lRes] = await Promise.all([
        api.listSecretBackends(),
        api.listSecrets(),
      ]);
      const bData = bRes?.data || bRes || {};
      setBackendsState({
        available: Array.isArray(bData.available) ? bData.available : [],
        default: bData.default || null,
        configuredForOrg: Array.isArray(bData.configuredForOrg) ? bData.configuredForOrg : [],
      });
      const lData = lRes?.data || lRes || {};
      setRecords(Array.isArray(lData.records) ? lData.records : []);
    } catch (e) {
      addToast({ tone: 'danger', title: 'Failed to load secrets',
        description: e?.message || String(e) });
      setBackendsState({ available: [], default: null });
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { reload(); }, [reload]);

  /* Aggregate counts per backend so the status tiles can show "N secrets". */
  const countsByBackend = useMemo(() => {
    const m = {};
    for (const r of records) {
      const k = r.backendId;
      m[k] = (m[k] || 0) + 1;
    }
    return m;
  }, [records]);

  /* Tile status carries three pieces of info:
   *   - isPlatformAvailable: yaml-driven platform-default credentials exist
   *   - isConfiguredForOrg : this org has stored its own credentials
   *   - isDefault          : platform-default selector
   * "Connected" badge fires if EITHER yaml or per-org config is present —
   * per-org config alone is sufficient for the backend to actually route. */
  const tiles = useMemo(() => KNOWN_BACKENDS.map((id) => {
    const meta = BACKEND_META[id];
    const isPlatformAvailable = backendsState.available.includes(id);
    const isConfiguredForOrg  = backendsState.configuredForOrg.includes(id);
    const isAvailable = isPlatformAvailable || isConfiguredForOrg;
    const isDefault = backendsState.default === id;
    const count = countsByBackend[id] || 0;
    return { id, meta, isAvailable, isPlatformAvailable, isConfiguredForOrg, isDefault, count };
  }), [backendsState, countsByBackend]);

  return (
    <div className="p-6">
      {/* ─── Header ────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[11px] text-v2-ink-muted uppercase tracking-wider font-semibold mb-1">
            Administer / Secrets Manager
          </div>
          <h1 className="text-[20px] font-semibold text-v2-ink flex items-center gap-2">
            <Key size={20} className="text-v2-ink-muted" />
            Secrets Manager
          </h1>
          <p className="text-[12px] text-v2-ink-muted mt-1 max-w-2xl leading-relaxed">
            Store credentials in your configured vault. References (not plaintext) are stamped onto
            connectors, agents, and skills across the org — rotate here without touching every consumer.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>
            Refresh
          </Button>
          {canWrite && (
            <Button
              variant="primary" size="sm" iconLeft={<Plus size={13} />}
              onClick={() => setAddOpen(true)}
              title="Store a new secret in the configured vault and get back a vault:// reference"
            >
              Add secret
            </Button>
          )}
        </div>
      </div>

      {/* ─── Backend status tiles ──────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 mb-5">
        {tiles.map(({ id, meta, isAvailable, isPlatformAvailable, isConfiguredForOrg, isDefault, count }) => {
          const Icon = meta.icon;
          const TileEl = canAdmin ? 'button' : 'div';
          const interactiveProps = canAdmin
            ? {
                type: 'button',
                onClick: () => setConfigureTarget(id),
                title: `Configure ${meta.label} for your org`,
                className:
                  'text-left bg-v2-surface border border-v2-border-subtle rounded-v2-md p-3.5 shadow-v2-sm ' +
                  'hover:border-v2-action hover:shadow-v2-md transition-colors v2-focus-ring',
              }
            : {
                className: 'bg-v2-surface border border-v2-border-subtle rounded-v2-md p-3.5 shadow-v2-sm',
              };
          // Sub-label tells the operator WHY a tile is Connected: their own
          // org config (preferred messaging) or the platform default. Inactive
          // tiles invite them to configure (admin) or surface why they can't.
          let subLabel;
          if (isConfiguredForOrg) {
            subLabel = 'Configured for your org';
          } else if (isPlatformAvailable) {
            subLabel = isDefault ? 'Platform default' : meta.note;
          } else if (canAdmin) {
            subLabel = 'Click to configure';
          } else {
            subLabel = 'Not configured';
          }
          return (
            <TileEl key={id} {...interactiveProps}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-[12px] text-v2-ink-muted">
                  <Icon size={14} className="text-v2-ink-muted" />
                  <span className="truncate">{meta.label}</span>
                </div>
                {isAvailable ? (
                  <Pill tone="success" size="sm">Connected</Pill>
                ) : (
                  <Pill tone="muted" size="sm">Not configured</Pill>
                )}
              </div>
              <div className="text-[18px] font-semibold text-v2-ink leading-tight">
                {count} {count === 1 ? 'secret' : 'secrets'}
              </div>
              <div className="text-[11px] text-v2-ink-muted mt-1 truncate">{subLabel}</div>
            </TileEl>
          );
        })}
      </div>

      {/* ─── Secrets table ─────────────────────────────────────────────── */}
      <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-v2-border-subtle">
          <div>
            <div className="text-[14px] font-semibold text-v2-ink">
              Secrets · {records.length} stored
            </div>
            <div className="text-[11.5px] text-v2-ink-muted mt-0.5">
              Visible to your org. Plaintext never leaves the vault.
            </div>
          </div>
        </div>

        {loading ? (
          <div className="px-4 py-10 text-center text-[12.5px] text-v2-ink-muted">
            Loading…
          </div>
        ) : records.length === 0 ? (
          <div className="p-8">
            <EmptyState
              icon={<Lock size={20} />}
              title="No secrets stored yet"
              description={canWrite
                ? 'Click Add secret to store your first credential in the configured vault. The reference returned is what you paste into Connector / Agent / Skill fields.'
                : 'Ask an Org Admin to store secrets here, then paste the returned vault:// references into your connectors.'}
              primaryAction={canWrite
                ? <Button variant="primary" iconLeft={<Plus size={13} />} onClick={() => setAddOpen(true)}>Add secret</Button>
                : null}
            />
          </div>
        ) : (
          <Table
            ariaLabel="Stored secrets"
            density="comfortable"
            columns={[
              {
                key: 'name', header: 'Name · scope',
                render: (r) => {
                  const { name, scope } = describePath(r.path);
                  return (
                    <div>
                      <div className="font-semibold text-v2-ink text-[13px]">{name}</div>
                      <div className="text-[11px] text-v2-ink-muted font-mono-v2 truncate mt-0.5">{scope}</div>
                    </div>
                  );
                },
              },
              {
                key: 'vault', header: 'Vault',
                render: (r) => {
                  const meta = BACKEND_META[r.backendId] || { label: r.backendId, tone: 'muted' };
                  return <Pill tone={meta.tone} size="sm">{meta.label}</Pill>;
                },
              },
              {
                key: 'created', header: 'Created',
                render: (r) => (
                  <span className="text-[12px] text-v2-ink-muted">{fmtRelative(r.createdAt)}</span>
                ),
              },
              {
                key: 'rotated', header: 'Rotated',
                render: (r) => (
                  <span className="text-[12px] text-v2-ink-muted">{fmtRelative(r.lastRotatedAt)}</span>
                ),
              },
              {
                key: 'actions', header: '',
                render: (r) => (
                  <div className="flex items-center gap-1 justify-end">
                    <CopyRefButton wireRef={r.wireRef} />
                    {canWrite && (
                      <Button
                        variant="ghost" size="sm" iconLeft={<RotateCcw size={12} />}
                        title="Store a new version of this secret"
                        onClick={() => setRotateTarget({
                          wireRef: r.wireRef,
                          displayName: describePath(r.path).name,
                        })}
                      />
                    )}
                    {canWrite && (
                      <Button
                        variant="ghost" size="sm" iconLeft={<Trash2 size={12} />}
                        title="Delete this secret"
                        onClick={() => setDeleteTarget({
                          wireRef: r.wireRef,
                          displayName: describePath(r.path).name,
                        })}
                      />
                    )}
                  </div>
                ),
              },
            ]}
            rows={records}
            getRowKey={(r) => r.wireRef}
          />
        )}
      </div>

      {/* ─── Helper banner ────────────────────────────────────────────── */}
      <div className="mt-4 px-4 py-3 rounded-v2-md border border-v2-border-subtle bg-v2-action-soft text-[12.5px] leading-relaxed text-v2-ink-secondary">
        <div className="flex items-start gap-2">
          <Info size={14} className="text-v2-action mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-semibold text-v2-ink">How references work.</span>{' '}
            When you configure a Connector, Agent, or Skill, paste the reference
            (e.g.{' '}
            <code className="font-mono-v2 text-[11.5px] bg-v2-surface px-1.5 py-0.5 rounded">
              vault://gcp/projects/acme/secrets/anthropic-api-key
            </code>
            ) in place of the plaintext key. The live value is resolved on each outbound call via{' '}
            <code className="font-mono-v2 text-[11.5px]">SecretManagerService.resolveIfReference</code>.
          </div>
        </div>
      </div>

      {/* ─── Add Secret slideout ───────────────────────────────────────── */}
      <AddSecretSlideout
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        backends={backendsState}
        onCreated={() => { setAddOpen(false); reload(); }}
      />

      {/* ─── Rotate modal ──────────────────────────────────────────────── */}
      <RotateSecretModal
        target={rotateTarget}
        onClose={() => setRotateTarget(null)}
        onRotated={() => { setRotateTarget(null); reload(); }}
      />

      {/* ─── Delete confirmation modal ─────────────────────────────────── */}
      <DeleteSecretModal
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => { setDeleteTarget(null); reload(); }}
      />

      {/* ─── Per-backend configure slideout (Phase 2) ──────────────────── */}
      <ConfigureBackendSlideout
        backendId={configureTarget}
        onClose={() => setConfigureTarget(null)}
        onSaved={() => { setConfigureTarget(null); reload(); }}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Sub-components
 * ═══════════════════════════════════════════════════════════════════════ */

function CopyRefButton({ wireRef }) {
  const { addToast } = useToast();
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard?.writeText(wireRef);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      addToast({ tone: 'warning', title: 'Clipboard unavailable',
        description: 'Copy the reference manually from the row.' });
    }
  };
  return (
    <Button
      variant="ghost" size="sm"
      iconLeft={copied ? <Check size={12} /> : <Copy size={12} />}
      title={copied ? 'Copied' : 'Copy reference'}
      onClick={onCopy}
    />
  );
}

/* Add-secret form: backend + scope + resource + plaintext → POST → returns
 * vault:// ref. Phase 2 layers an Org/Tenant scope picker; for now we accept
 * an explicit org id and let the operator pick the resource type. */
function AddSecretSlideout({ isOpen, onClose, backends, onCreated }) {
  const { addToast } = useToast();
  const [form, setForm] = useState({
    backend: '',
    scope: 'org',         // 'org' | 'tenant'
    tenantId: '',
    resourceType: 'connectors',
    resourceId: '',
    fieldName: 'api-key',
    plaintext: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [revealPlaintext, setRevealPlaintext] = useState(false);
  const [createdRef, setCreatedRef] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setForm((prev) => ({ ...prev, backend: backends.default || prev.backend }));
      setCreatedRef(null);
    }
  }, [isOpen, backends.default]);

  if (!isOpen) return null;

  const canSubmit = form.backend
    && form.resourceType?.trim()
    && form.resourceId?.trim()
    && form.fieldName?.trim()
    && form.plaintext?.trim();

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        backend: form.backend,
        // Phase 1 — scope is encoded into resourceType/resourceId/fieldName via
        // SecretManagerService.store's path convention. Org-default is the empty
        // tenantId; passing a tenantId locks the resource to that tenant.
        tenantId: form.scope === 'tenant' ? form.tenantId.trim() : null,
        resourceType: form.resourceType.trim(),
        resourceId: form.resourceId.trim(),
        fieldName: form.fieldName.trim(),
        plaintext: form.plaintext,
      };
      const res = await api.storeSecret(payload);
      const ref = res?.data?.ref || res?.ref;
      if (!ref) {
        addToast({ tone: 'danger', title: 'Store failed',
          description: res?.data?.error || res?.error || 'Backend did not return a reference' });
        return;
      }
      setCreatedRef(ref);
      addToast({ tone: 'success', title: 'Secret stored',
        description: 'Copy the reference and paste it into your connector / agent / skill.' });
    } catch (e) {
      addToast({ tone: 'danger', title: 'Store failed', description: e?.message || String(e) });
    } finally {
      setSubmitting(false);
    }
  };

  const onCopyRef = async () => {
    try { await navigator.clipboard?.writeText(createdRef); } catch (e) { /* ignore */ }
  };

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={520}
      ariaLabel="Add secret"
      header={(
        <div>
          <h2 className="text-[16px] font-semibold tracking-tight m-0">Add secret</h2>
          <p className="text-[11.5px] text-v2-ink-secondary mt-1 m-0">
            Plaintext goes into the configured vault. We persist only the reference.
          </p>
        </div>
      )}
      footer={createdRef ? (
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCreated}>Done</Button>
        </div>
      ) : (
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary" size="sm"
            disabled={!canSubmit || submitting}
            loading={submitting}
            onClick={onSubmit}
          >
            {submitting ? 'Storing…' : 'Store secret'}
          </Button>
        </div>
      )}
    >
      {createdRef ? (
        <div className="p-1">
          <div className="border border-v2-border-subtle bg-v2-action-soft rounded-v2-md p-3">
            <div className="flex items-center gap-2 mb-2">
              <Check size={14} className="text-v2-success" />
              <span className="text-[13px] font-semibold text-v2-ink">Secret stored. Use this reference:</span>
            </div>
            <code className="block bg-v2-surface border border-v2-border rounded-v2-sm px-2 py-1.5 text-[12px] font-mono-v2 text-v2-ink break-all">
              {createdRef}
            </code>
            <Button variant="secondary" size="sm" iconLeft={<Copy size={12} />} className="mt-2" onClick={onCopyRef}>
              Copy reference
            </Button>
          </div>
          <p className="text-[12px] text-v2-ink-muted mt-3 leading-relaxed">
            Paste this string into the API Key field of a Connector / Agent / Skill instead of the
            plaintext key. The platform resolves the live value on each outbound call.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Backend picker */}
          <div>
            <label className="block text-[12px] font-semibold text-v2-ink mb-1.5">Vault backend</label>
            <select
              className="v2-focus-ring w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[13px]"
              value={form.backend}
              onChange={(e) => setForm((f) => ({ ...f, backend: e.target.value }))}
              disabled={submitting}
            >
              <option value="">— Select a backend —</option>
              {backends.available.map((id) => (
                <option key={id} value={id}>
                  {(BACKEND_META[id]?.label) || id}{id === backends.default ? ' (default)' : ''}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-v2-ink-muted mt-1">
              Not-configured backends are hidden. To add one, configure it on the Secrets Manager backend tiles above.
            </p>
          </div>

          {/* Scope */}
          <div>
            <label className="block text-[12px] font-semibold text-v2-ink mb-1.5">Sharing scope</label>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-[12.5px] cursor-pointer">
                <input
                  type="radio" name="scope"
                  checked={form.scope === 'org'}
                  onChange={() => setForm((f) => ({ ...f, scope: 'org' }))}
                  disabled={submitting}
                />
                <span>Org-wide</span>
              </label>
              <label className="flex items-center gap-1.5 text-[12.5px] cursor-pointer ml-3">
                <input
                  type="radio" name="scope"
                  checked={form.scope === 'tenant'}
                  onChange={() => setForm((f) => ({ ...f, scope: 'tenant' }))}
                  disabled={submitting}
                />
                <span>Tenant-locked</span>
              </label>
            </div>
            {form.scope === 'tenant' && (
              <input
                type="text"
                placeholder="Tenant ID (UUID)"
                className="v2-focus-ring mt-2 w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[12.5px] font-mono-v2"
                value={form.tenantId}
                onChange={(e) => setForm((f) => ({ ...f, tenantId: e.target.value }))}
                disabled={submitting}
              />
            )}
          </div>

          {/* Resource type / id / field */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[12px] font-semibold text-v2-ink mb-1.5">Resource type</label>
              <input
                type="text"
                className="v2-focus-ring w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[12.5px]"
                value={form.resourceType}
                placeholder="connectors"
                onChange={(e) => setForm((f) => ({ ...f, resourceType: e.target.value }))}
                disabled={submitting}
              />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-v2-ink mb-1.5">Field name</label>
              <input
                type="text"
                className="v2-focus-ring w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[12.5px]"
                value={form.fieldName}
                placeholder="api-key"
                onChange={(e) => setForm((f) => ({ ...f, fieldName: e.target.value }))}
                disabled={submitting}
              />
            </div>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-v2-ink mb-1.5">Resource ID</label>
            <input
              type="text"
              className="v2-focus-ring w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[12.5px] font-mono-v2"
              value={form.resourceId}
              placeholder="anthropic OR a connector UUID"
              onChange={(e) => setForm((f) => ({ ...f, resourceId: e.target.value }))}
              disabled={submitting}
            />
            <p className="text-[11px] text-v2-ink-muted mt-1">
              Becomes a segment in the vault path; usually the connector name (e.g. "anthropic") or its UUID.
            </p>
          </div>

          {/* Plaintext */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[12px] font-semibold text-v2-ink">Plaintext secret</label>
              <button
                type="button"
                className="text-[11px] text-v2-action hover:underline flex items-center gap-1"
                onClick={() => setRevealPlaintext((v) => !v)}
              >
                {revealPlaintext ? <><EyeOff size={11} /> Hide</> : <><Eye size={11} /> Reveal</>}
              </button>
            </div>
            <textarea
              rows={3}
              className="v2-focus-ring w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[12.5px] font-mono-v2"
              value={form.plaintext}
              placeholder={revealPlaintext ? 'sk-ant-...' : '••••••••••••••••'}
              onChange={(e) => setForm((f) => ({ ...f, plaintext: e.target.value }))}
              style={revealPlaintext ? null : { WebkitTextSecurity: 'disc' }}
              disabled={submitting}
            />
            <p className="text-[11px] text-v2-ink-muted mt-1">
              Sent over TLS, encrypted at rest in the selected backend. Never logged.
            </p>
          </div>
        </div>
      )}
    </Slideout>
  );
}

function RotateSecretModal({ target, onClose, onRotated }) {
  const { addToast } = useToast();
  const [newPlaintext, setNewPlaintext] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [revealPlaintext, setRevealPlaintext] = useState(false);

  useEffect(() => {
    if (target) { setNewPlaintext(''); setRevealPlaintext(false); setSubmitting(false); }
  }, [target]);

  if (!target) return null;

  const onSubmit = async () => {
    if (!newPlaintext?.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.rotateSecret(target.wireRef, newPlaintext);
      const ref = res?.data?.ref || res?.ref;
      if (!ref) {
        addToast({ tone: 'danger', title: 'Rotate failed',
          description: res?.data?.error || res?.error || 'Backend did not return a reference' });
        return;
      }
      addToast({ tone: 'success', title: 'Secret rotated',
        description: `Latest version stored. Consumers using ${target.displayName} pick it up automatically.` });
      onRotated && onRotated();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Rotate failed', description: e?.message || String(e) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={!!target}
      onClose={submitting ? null : onClose}
      title={`Rotate ${target.displayName}`}
      size="md"
      footer={(
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" size="sm"
                  disabled={submitting || !newPlaintext?.trim()}
                  loading={submitting}
                  onClick={onSubmit}>
            {submitting ? 'Rotating…' : 'Rotate'}
          </Button>
        </div>
      )}
    >
      <p className="text-[12.5px] text-v2-ink-secondary mb-3">
        Store a new version under the same reference. Existing consumers using{' '}
        <code className="font-mono-v2 text-[11.5px] bg-v2-muted px-1.5 py-0.5 rounded">{target.wireRef}</code>{' '}
        will pick up the new value on their next call — no consumer-side change needed.
      </p>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[12px] font-semibold text-v2-ink">New plaintext</label>
        <button
          type="button"
          className="text-[11px] text-v2-action hover:underline flex items-center gap-1"
          onClick={() => setRevealPlaintext((v) => !v)}
        >
          {revealPlaintext ? <><EyeOff size={11} /> Hide</> : <><Eye size={11} /> Reveal</>}
        </button>
      </div>
      <textarea
        rows={3}
        className="v2-focus-ring w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[12.5px] font-mono-v2"
        value={newPlaintext}
        placeholder={revealPlaintext ? 'new-value-here' : '••••••••••••••••'}
        onChange={(e) => setNewPlaintext(e.target.value)}
        style={revealPlaintext ? null : { WebkitTextSecurity: 'disc' }}
        disabled={submitting}
      />
    </Modal>
  );
}

/* ───────────────────────────────────────────────────────────────────────
 * ConfigureBackendSlideout (Phase 2) — per-vendor configuration form.
 *
 * Reads /api/v1/secrets/backends/{id}/config on open (server returns sensitive
 * fields masked with the SENTINEL). On save, the sentinel round-trip is
 * preserved by the backend — the operator can edit non-secret fields (project
 * id, region, vault URL) without re-entering the secret.
 *
 * Phase 2 saves the config and surfaces it on the tile. Phase 3 wires these
 * configs into the live SDK clients so the credentials actually drive vault
 * calls — until then the banner on the page makes that explicit.
 * ─────────────────────────────────────────────────────────────────────── */

const SENTINEL_DOTS = '••••••••';

/* Per-backend field schemas. `secret: true` triggers the reveal/hide toggle
 * and the SENTINEL round-trip; `multiline: true` swaps the input for a textarea
 * (used for service-account JSON). Order in the array is the render order. */
const BACKEND_FIELD_SCHEMAS = {
  local: [
    { key: 'masterKey', label: 'Master key (base64, 32 bytes)', secret: true,
      help: 'Leave blank to keep the auto-bootstrap process-local key. Set a new value to rotate the encryption key your org uses for Local secrets — existing entries stay readable until you rotate them individually.' },
  ],
  gcp: [
    { key: 'projectId', label: 'GCP project ID',
      placeholder: 'my-gcp-project',
      help: 'The project whose Secret Manager holds these secrets.' },
    { key: 'serviceAccountJson', label: 'Service account JSON', secret: true, multiline: true,
      placeholder: '{"type":"service_account",...}',
      help: 'Contents of the SA key file. Requires roles/secretmanager.admin.' },
  ],
  hashicorp: [
    { key: 'address', label: 'Vault address',
      placeholder: 'https://vault.example.com',
      help: 'Base URL of your Vault cluster.' },
    { key: 'token', label: 'Vault token', secret: true,
      placeholder: 'hvs.…',
      help: 'Token with read/write on the KV mount below.' },
    { key: 'kvMount', label: 'KV v2 mount path',
      placeholder: 'secret', help: 'Default is "secret"; set if your KV v2 engine is mounted elsewhere.' },
    { key: 'namespace', label: 'Namespace (Enterprise)',
      placeholder: 'admin/finance', help: 'Optional — Vault Enterprise namespaces only.' },
  ],
  aws: [
    { key: 'region', label: 'AWS region',
      placeholder: 'us-east-1' },
    { key: 'roleArn', label: 'Role ARN (recommended)',
      placeholder: 'arn:aws:iam::123456789012:role/agentlets-secrets',
      help: 'Preferred — STS assume-role from the platform\'s instance role.' },
    { key: 'accessKeyId', label: 'Access key ID', secret: true,
      placeholder: 'AKIA…',
      help: 'Only if role ARN isn\'t usable. Treated as sensitive.' },
    { key: 'secretAccessKey', label: 'Secret access key', secret: true,
      placeholder: '…',
      help: 'Paired with Access key ID above.' },
  ],
  azure: [
    { key: 'vaultUrl', label: 'Key Vault URL',
      placeholder: 'https://my-vault.vault.azure.net' },
    { key: 'tenantId', label: 'Azure AD tenant ID',
      placeholder: '00000000-0000-0000-0000-000000000000' },
    { key: 'clientId', label: 'Service principal client ID',
      placeholder: '00000000-0000-0000-0000-000000000000' },
    { key: 'clientSecret', label: 'Service principal client secret', secret: true,
      placeholder: '…',
      help: 'OR set up workload identity and leave blank.' },
  ],
};

function ConfigureBackendSlideout({ backendId, onClose, onSaved }) {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({});
  const [hasConfig, setHasConfig] = useState(false);
  const [revealed, setRevealed] = useState({});

  const schema = backendId ? BACKEND_FIELD_SCHEMAS[backendId] : null;
  const meta = backendId ? BACKEND_META[backendId] : null;

  useEffect(() => {
    if (!backendId) return;
    setLoading(true);
    setRevealed({});
    api.getSecretBackendConfig(backendId)
      .then((res) => {
        const data = res?.data || res || {};
        const remoteConfig = (data.config || {});
        const merged = {};
        for (const f of schema || []) {
          merged[f.key] = remoteConfig[f.key] != null ? String(remoteConfig[f.key]) : '';
        }
        setForm(merged);
        setHasConfig(!!data.hasConfig);
      })
      .catch((e) => {
        addToast({ tone: 'danger', title: 'Failed to load config',
          description: e?.message || String(e) });
      })
      .finally(() => setLoading(false));
  }, [backendId, schema, addToast]);

  if (!backendId || !schema || !meta) return null;

  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const toggleReveal = (key) => setRevealed((prev) => ({ ...prev, [key]: !prev[key] }));

  const onSave = async () => {
    setSubmitting(true);
    try {
      // Build payload: include every field the schema declares; sentinel-protected
      // fields where the user left the SENTINEL untouched get sent as the sentinel
      // (server-side merges with the prior stored value).
      const payload = {};
      for (const f of schema) {
        const v = form[f.key];
        if (v == null || v === '') continue;
        payload[f.key] = v;
      }
      const res = await api.saveSecretBackendConfig(backendId, payload);
      if (!res || res.ok === false || res.data?.ok === false) {
        const err = res?.data?.error || res?.error || `HTTP ${res?.status || '?'}`;
        addToast({ tone: 'danger', title: 'Save failed', description: err });
        return;
      }
      addToast({ tone: 'success', title: `${meta.label} configured`,
        description: 'Stored encrypted. Secrets stored under this backend will now route through your org\'s credentials.' });
      onSaved && onSaved();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Save failed', description: e?.message || String(e) });
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async () => {
    setDeleting(true);
    try {
      await api.deleteSecretBackendConfig(backendId);
      addToast({ tone: 'success', title: 'Configuration removed',
        description: `${meta.label} falls back to yaml on next resolution.` });
      onSaved && onSaved();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Delete failed', description: e?.message || String(e) });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Slideout
      isOpen={!!backendId}
      onClose={submitting || deleting ? null : onClose}
      width={540}
      ariaLabel={`Configure ${meta.label}`}
      header={(
        <div>
          <div className="text-[11px] text-v2-ink-muted uppercase tracking-wider font-semibold mb-1">
            Vault backend
          </div>
          <h2 className="text-[16px] font-semibold tracking-tight m-0">Configure {meta.label}</h2>
          <p className="text-[11.5px] text-v2-ink-secondary mt-1 m-0">
            Stored encrypted at rest. Sensitive fields are masked on re-open.
          </p>
        </div>
      )}
      footer={(
        <div className="flex items-center justify-between gap-2 w-full">
          {hasConfig ? (
            <Button variant="ghost" size="sm" iconLeft={<Trash2 size={12} />}
                    onClick={onDelete} disabled={submitting || deleting}
                    loading={deleting}>
              Remove config
            </Button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} disabled={submitting || deleting}>
              Cancel
            </Button>
            <Button variant="primary" size="sm"
                    onClick={onSave}
                    disabled={submitting || deleting || loading}
                    loading={submitting}>
              {submitting ? 'Saving…' : 'Save configuration'}
            </Button>
          </div>
        </div>
      )}
    >
      {loading ? (
        <div className="px-1 py-6 text-center text-[12.5px] text-v2-ink-muted">Loading…</div>
      ) : (
        <div className="space-y-4">
          <div className="px-3 py-2 rounded-v2-sm bg-v2-action-soft border border-v2-border-subtle text-[11.5px] leading-relaxed text-v2-ink-secondary">
            <span className="font-semibold text-v2-ink">{meta.label}.</span>{' '}{meta.note}
          </div>

          {schema.map((f) => {
            const isSecret = !!f.secret;
            const showSentinel = isSecret && hasConfig && (form[f.key] === SENTINEL_DOTS);
            const reveal = !!revealed[f.key];
            return (
              <div key={f.key}>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[12px] font-semibold text-v2-ink">{f.label}</label>
                  {isSecret && !showSentinel && (
                    <button type="button"
                            className="text-[11px] text-v2-action hover:underline flex items-center gap-1"
                            onClick={() => toggleReveal(f.key)}>
                      {reveal ? <><EyeOff size={11} /> Hide</> : <><Eye size={11} /> Reveal</>}
                    </button>
                  )}
                </div>
                {f.multiline ? (
                  <textarea
                    rows={5}
                    className="v2-focus-ring w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[12px] font-mono-v2"
                    value={form[f.key] || ''}
                    placeholder={f.placeholder || ''}
                    onChange={(e) => updateField(f.key, e.target.value)}
                    style={(isSecret && !reveal && !showSentinel) ? { WebkitTextSecurity: 'disc' } : null}
                    disabled={submitting || deleting}
                  />
                ) : (
                  <input
                    type={(isSecret && !reveal) ? 'password' : 'text'}
                    className="v2-focus-ring w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[12.5px] font-mono-v2"
                    value={form[f.key] || ''}
                    placeholder={f.placeholder || ''}
                    onChange={(e) => updateField(f.key, e.target.value)}
                    disabled={submitting || deleting}
                  />
                )}
                {f.help && (
                  <p className="text-[11px] text-v2-ink-muted mt-1">{f.help}</p>
                )}
                {showSentinel && (
                  <p className="text-[11px] text-v2-ink-muted mt-1 italic">
                    Stored value preserved. Clear the field and type a new value to overwrite.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Slideout>
  );
}

function DeleteSecretModal({ target, onClose, onDeleted }) {
  const { addToast } = useToast();
  const [typed, setTyped] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (target) { setTyped(''); setSubmitting(false); } }, [target]);

  if (!target) return null;

  const canDelete = typed.trim() === target.displayName;

  const onSubmit = async () => {
    if (!canDelete) return;
    setSubmitting(true);
    try {
      await api.deleteSecret(target.wireRef);
      addToast({ tone: 'success', title: 'Secret deleted',
        description: `${target.displayName} removed from the vault.` });
      onDeleted && onDeleted();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Delete failed', description: e?.message || String(e) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={!!target}
      onClose={submitting ? null : onClose}
      title="Delete secret"
      size="md"
      footer={(
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="danger" size="sm"
                  disabled={!canDelete || submitting}
                  loading={submitting}
                  onClick={onSubmit}>
            {submitting ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      )}
    >
      <div className="flex items-start gap-2 mb-3 p-2.5 rounded-v2-md bg-v2-danger-soft border border-v2-danger-subtle">
        <AlertTriangle size={14} className="text-v2-danger mt-0.5 flex-shrink-0" />
        <div className="text-[12px] text-v2-ink-secondary leading-relaxed">
          This deletes the secret from the vault. Any Connector / Agent / Skill still bound to{' '}
          <code className="font-mono-v2 text-[11px] bg-v2-surface px-1 py-0.5 rounded">{target.wireRef}</code>{' '}
          will start failing with a resolution error on its next outbound call. Audit the
          consumers before proceeding.
        </div>
      </div>
      <p className="text-[12.5px] text-v2-ink-secondary mb-2">
        Type <strong className="text-v2-ink">{target.displayName}</strong> to confirm:
      </p>
      <input
        type="text"
        className="v2-focus-ring w-full bg-v2-surface border border-v2-border rounded-v2-md px-2.5 py-1.5 text-[12.5px] font-mono-v2"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        disabled={submitting}
        autoFocus
      />
    </Modal>
  );
}
